import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Images } from "@/constants/images";
import { daysOfWeek, monthNames, presetDates, yearList } from "@/constants/app_constants.ts";
import { Select } from "antd";

type CalendarRangeProps = {
  usingCurrentMonth?: boolean;
  setActiveLabel?: (label: string | null) => void;
  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;
  setStartDate: (date: dayjs.Dayjs) => void;
  setEndDate: (date: dayjs.Dayjs) => void;
};
export default function CalendarRange({
  usingCurrentMonth = false,
  setActiveLabel,
  startDate,
  endDate,
  setStartDate,
  setEndDate
}: CalendarRangeProps) {
  const today = dayjs();

  const [leftMonth, setLeftMonth] = useState(() => (startDate ?? today).startOf("month"));
  const [rightMonth, setRightMonth] = useState(() => (startDate ?? today).add(1, "month").startOf("month"));

  useEffect(() => {
    if (!startDate || !endDate) return;

    const matchedPreset = presetDates.find(preset => {
      const [presetStart, presetEnd] = preset.range;
      return startDate.isSame(presetStart, "day") && endDate.isSame(presetEnd, "day");
    });

    setActiveLabel?.(matchedPreset ? matchedPreset.label : "Custom");
  }, [startDate, endDate, setActiveLabel]);

  const handleDateClick = (date: Dayjs) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null as unknown as Dayjs); // Clear end
    } else if (date.isBefore(startDate)) {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };

  useEffect(() => {
    if (startDate) {
      setLeftMonth(startDate.startOf("month"));
      setRightMonth(startDate.add(1, "month").startOf("month"));
    }
  }, [startDate]);

  const isInRange = (date: Dayjs) => {
    if (!startDate || !endDate) return false;
    return date.isAfter(startDate) && date.isBefore(endDate);
  };

  const isSelected = (date: Dayjs) => {
    return (startDate && date.isSame(startDate, "day")) || (endDate && date.isSame(endDate, "day"));
  };

  const isToday = (date: Dayjs) => {
    return date.isSame(today, "day");
  };

  const renderMonth = (monthStart: Dayjs, onPrev: () => void, onNext: () => void, isFirstMonth?: boolean) => {
    const startOfCalendar = monthStart.startOf("month").startOf("week");
    const days = [];

    for (let i = 0; i < 42; i++) {
      const date = startOfCalendar.add(i, "day");
      const isCurrentMonth = date.month() === monthStart.month();

      if (!isCurrentMonth) {
        days.push(<div key={date.toString()} />); // Empty slot
        continue;
      }

      days.push(
        <button
          key={date.toString()}
          onClick={() => handleDateClick(date)}
          className={`btn-calendar w-full h-full aspect-square flex items-center justify-center text-sm rounded transition
            ${isSelected(date) ? "bg-orange-600 text-white" : isInRange(date) ? "bg-gray-200" : ""}
            ${!isCurrentMonth ? "text-gray-400" : ""}
            ${isToday(date) ? "border border-orange-400" : ""}            
          `}
        >
          {date.date()}
        </button>
      );
    }

    const onMonthOrYearChanged = (date: Dayjs) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      isFirstMonth ? setLeftMonth(date) : setRightMonth(date);
    };

    return (
      <div
        className={`flex flex-col h-full space-y-1 pr-4 ${isFirstMonth ? "border-r border-r-gray-300" : ""} ${usingCurrentMonth ? "w-full border-0 border-r-0" : "w-[300px]"} `}
      >
        {/* Header Month */}
        <div className="flex items-center justify-between mb-2">
          <button onClick={onPrev} className="btn-calendar p-1 !text-[16px] text-gray-500 hover:text-black">
            <img src={Images.IconCalendarLeft} alt="" />
          </button>

          <div className="w-[130px] px-2 py-1">
            <Select
              className="w-full"
              value={monthStart.month()}
              onChange={e => {
                onMonthOrYearChanged(monthStart.month(e));
              }}
              getPopupContainer={triggerNode => triggerNode.parentNode}
            >
              {monthNames.map((name: string, idx: number) => (
                <Select.Option key={idx} value={idx}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="w-[100px]">
            <Select
              className="w-full"
              value={monthStart.year()}
              onChange={e => {
                onMonthOrYearChanged(monthStart.year(e));
              }}
              getPopupContainer={triggerNode => triggerNode.parentNode}
            >
              {yearList.map((year: number) => (
                <Select.Option key={year} value={year}>
                  {year}
                </Select.Option>
              ))}
            </Select>
          </div>

          {/*<div className="font-semibold">{monthStart.format("MMMM YYYY")}</div>*/}
          <button onClick={onNext} className="btn-calendar p-1 !text-[16px] text-gray-500 hover:text-black">
            <img src={Images.IconCalendarRight} alt="" />
          </button>
        </div>

        {/* Days of week */}
        <div className="grid grid-cols-7 text-xs text-gray-900 mb-1">
          {daysOfWeek.map(day => (
            <div key={day} className="text-center">
              {day}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-1 text-sm">{days}</div>
      </div>
    );
  };

  return (
    <div className="flex space-x-4 p-2 items-center h-full max-w-full">
      {renderMonth(
        leftMonth,
        () => setLeftMonth(leftMonth.subtract(1, "month")),
        () => setLeftMonth(leftMonth.add(1, "month")),
        true
      )}

      {!usingCurrentMonth &&
        renderMonth(
          rightMonth,
          () => setRightMonth(rightMonth.subtract(1, "month")),
          () => setRightMonth(rightMonth.add(1, "month")),
          false
        )}
    </div>
  );
}
